import { ChevronDownIcon } from '@otter-ai/den-icons';
import { cx } from 'cva';
import type { ReactElement } from 'react';
import { Button, type ButtonProps } from '../../components/Button';
import {
  SelectContent,
  SelectItem,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from '../../components/Select';
import { stringifyValue } from '../../utils/stringify-value';
import { useAssertValue } from '../../utils/use-assert-value';

export type SelectßOption = {
  /**
   * The underlying value of the option.
   */
  value: string | number;
  /**
   * The text displayed for the option.
   */
  label: string;
  /**
   * description for the multiline options.
   * This is only used if the `multiline` prop is `true`.
   */
  description?: string | ReactElement;
  /**
   * Icon to display next to the option
   */
  icon?: React.ReactNode;

  /**
   * Is the option disabled?
   * The option is displayed to the user but the user cannot pick it.
   */
  disabled?: boolean;
};

export type SelectßProps = {
  /**
   * Array of options for the select dropdown
   */
  options: SelectßOption[];
  /**
   * Callback for when the value changes
   */
  onChange: (value: string) => void;
  /**
   * Callback for when the dropdown opens or closes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether the select is disabled
   */
  disabled?: boolean;
  /**
   * The text to display in the closed state when no value is selected
   */
  placeholder: string;

  /**
   * The value of the selected option
   */
  value: string | number | undefined;

  /**
   * The icon to display at the start of the button
   */
  iconStart?: React.ReactNode;

  /**
   * Whether the select is multiline
   */
  multiline?: boolean;

  /**
   * whether to show chevron end button
   */
  showChevronEnd?: boolean;

  /**
   * Additional class names to apply to the select container
   */
  className?: string;

  /**
   * Test identifier for testing purposes
   */
  'data-testid'?: string;
} & Pick<ButtonProps, 'size' | 'shape' | 'variant' | 'version'>;

/**
 * A simple select component that allows users to select a single option from a list of options.
 *
 * @example
 * function Example() {
 *   const [value, setValue] = useState<string | undefined>();
 *   const options = [
 *     { value: 'apple', label: 'Apple' },
 *     { value: 'banana', label: 'Banana' },
 *   ];
 *  return (<Selectß options={options} value={value} onChange={setValue} placeholder="Select a fruit" />);
 * };
 */
export function Selectß({
  options,
  value,
  onChange,
  onOpenChange,
  size,
  shape,
  variant,
  version,
  disabled = false,
  placeholder,
  iconStart,
  multiline,
  showChevronEnd = true,
  className,
  'data-testid': testId = 'select',
  ...rest
}: SelectßProps) {
  useAssertValue(value, options);

  return (
    <SelectRoot
      onValueChange={onChange}
      onOpenChange={onOpenChange}
      value={stringifyValue(value)}
      data-testid={testId}
      disabled={disabled}
    >
      <SelectTrigger asChild>
        <Button
          iconEnd={showChevronEnd && <ChevronDownIcon />}
          iconStart={iconStart}
          size={size}
          shape={shape}
          disabled={disabled}
          variant={variant}
          version={version}
          className={className}
          aria-label="selectButtonTrigger"
          data-testid={`${testId}-trigger`}
          {...rest}
        >
          <SelectValue placeholder={placeholder} asChild>
            <span className="truncate" data-testid={`${testId}-value`}>
              {options.find(
                (option) =>
                  stringifyValue(option.value) === stringifyValue(value),
              )?.label ?? placeholder}
            </span>
          </SelectValue>
        </Button>
      </SelectTrigger>

      <SelectPortal>
        <SelectContent position="popper">
          <SelectScrollUpButton />
          <SelectViewport>
            {options.map(
              ({
                value: itemVal,
                label,
                description,
                icon,
                disabled: optionDisabled,
              }) => {
                if (multiline) {
                  return (
                    <SelectItem
                      key={itemVal}
                      value={stringifyValue(itemVal)}
                      disabled={optionDisabled}
                    >
                      <MultilineSelectOptionContent
                        itemVal={itemVal}
                        label={label}
                        description={description ?? ''}
                        testId={testId}
                        disabled={optionDisabled}
                      />
                    </SelectItem>
                  );
                }

                return (
                  <SelectItem
                    data-testid={`${testId}-item-${itemVal}`}
                    key={itemVal}
                    value={stringifyValue(itemVal)}
                    disabled={optionDisabled}
                  >
                    <div className="flex items-center gap-2">
                      {icon && <span className="flex-shrink-0">{icon}</span>}
                      {label}
                    </div>
                  </SelectItem>
                );
              },
            )}
          </SelectViewport>
          <SelectScrollDownButton data-testid={`${testId}-scroll-down`} />
        </SelectContent>
      </SelectPortal>
    </SelectRoot>
  );
}

function MultilineSelectOptionContent({
  itemVal,
  label,
  description,
  testId,
  disabled = false,
  icon,
}: {
  itemVal: string | number;
  label: string;
  description: string | ReactElement;
  testId: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col justify-center items-start py-2">
      <div className="flex items-center gap-2 w-full">
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <p
          data-testid={`${testId}-item-label-${itemVal}`}
          className="text-sm font-semibold overflow-hidden text-ellipsis w-full truncate"
        >
          {label}
        </p>
      </div>
      <p
        data-testid={`${testId}-item-label-${itemVal}-description`}
        className={cx(
          'text-xs font-regular overflow-hidden text-ellipsis',
          disabled ? 'text-state-disabled' : 'text-subtle',
        )}
      >
        {description}
      </p>
    </div>
  );
}
